"use client";

/**
 * ExtensionMock — animated mock of the askFinz browser extension surface
 * for /extension.
 *
 * Visual:
 *   - Stylised browser frame with a URL bar that swaps between three real-
 *     looking pages (news article, email thread, spreadsheet).
 *   - The askFinz side panel is docked to the right (~360px) with its own
 *     chrome: monogram + model picker + close button.
 *   - Inside the panel: a context-relevant question, a streamed reply with
 *     inline citations to the page, and a model picker the user flips
 *     mid-conversation from "Frontier · fast" to "Reasoning".
 *
 * Loop:
 *   Every ~9s the underlying tab swaps and the panel conversation resets to
 *   a new context-relevant exchange. Reply streams in. Loop runs forever.
 *
 * Pure presentation. Lives in askfinz.com only.
 */
import { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Brain,
  ChevronDown,
  X,
  ArrowLeft,
  ArrowRight,
  RotateCw,
  Star,
  Send,
  AtSign,
  Paperclip,
  Newspaper,
  Mail,
  Table,
} from "lucide-react";

type ModelOpt = {
  id: "frontier-fast" | "reasoning" | "open-weight";
  label: string;
  icon: typeof Sparkles;
};

const MODELS: ModelOpt[] = [
  { id: "frontier-fast", label: "Frontier · fast", icon: Sparkles },
  { id: "reasoning",     label: "Reasoning",       icon: Brain },
  { id: "open-weight",   label: "Open-weight",     icon: Sparkles },
];

type Scene = {
  id: "news" | "email" | "sheet";
  url: string;
  pageIcon: typeof Newspaper;
  pageTitle: string;
  pageKicker: string;
  question: string;
  modelStart: ModelOpt;
  modelSwitch?: ModelOpt;
  // Each chunk renders in sequence; "cite" chunks get the citation pill style.
  reply: { kind: "text" | "cite"; text: string }[];
};

const SCENES: Scene[] = [
  {
    id: "news",
    url: "reuters.com/article/2026/q1-chip-equipment-spend",
    pageIcon: Newspaper,
    pageTitle: "Chip-equipment spending hits five-year high in Q1",
    pageKicker: "Reuters · Technology",
    question: "Summarise the key risk in this piece.",
    modelStart: MODELS[0],
    modelSwitch: MODELS[1],
    reply: [
      { kind: "text", text: "The headline risk is concentration: " },
      { kind: "cite", text: "[§3]" },
      { kind: "text", text: " three buyers account for ~71% of Q1 equipment orders, leaving the supply chain exposed if any of them throttles capex. The piece also flags " },
      { kind: "cite", text: "[§5]" },
      { kind: "text", text: " an ASML export-licence renewal due in June as a near-term inflection." },
    ],
  },
  {
    id: "email",
    url: "mail.google.com/u/0/inbox/thread/partnership-q3",
    pageIcon: Mail,
    pageTitle: "Re: Q3 partnership — final pass",
    pageKicker: "Inbox · Maya Iqbal",
    question: "What are the two open questions in this thread?",
    modelStart: MODELS[0],
    modelSwitch: MODELS[1],
    reply: [
      { kind: "text", text: "Two flags on " },
      { kind: "cite", text: "[§4.2]" },
      { kind: "text", text: ": (1) the IP carve-out wording — Maya wants tighter language than your draft; (2) the renewal cadence — she's pushing for rolling 30, you proposed 12 months with 60-day notice. Everything else " },
      { kind: "cite", text: "[¶3]" },
      { kind: "text", text: " reads cleanly." },
    ],
  },
  {
    id: "sheet",
    url: "docs.google.com/spreadsheets/d/cohort-retention-q1/edit",
    pageIcon: Table,
    pageTitle: "Cohort retention · Q1 2026",
    pageKicker: "Sheets · 4 tabs",
    question: "Which cohort is underperforming and by how much?",
    modelStart: MODELS[0],
    modelSwitch: MODELS[1],
    reply: [
      { kind: "text", text: "March cohort " },
      { kind: "cite", text: "[Tab 2 · row 14]" },
      { kind: "text", text: " is the outlier — week-4 retention sits at 38% vs. the Jan/Feb baseline of 51%. The drop concentrates on free-plan signups; paid retention " },
      { kind: "cite", text: "[Tab 3]" },
      { kind: "text", text: " is broadly flat." },
    ],
  },
];

export function ExtensionMock() {
  const [sceneIdx, setSceneIdx] = useState(0);
  const [model, setModel] = useState<ModelOpt>(SCENES[0].modelStart);
  // First-paint: question + full first answer visible so the side panel never
  // shows an empty conversation.
  const [streamLen, setStreamLen] = useState(SCENES[0].reply.length);
  const [showQuestion, setShowQuestion] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);
  const firstRun = useRef(true);

  const scene = SCENES[sceneIdx];
  const totalChunks = scene.reply.length;

  // Drive the scripted loop forever.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimers = () => {
      timers.current.forEach((id) => clearTimeout(id));
      timers.current = [];
    };
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timers.current.push(id);
    };

    const runScene = (i: number) => {
      const sc = SCENES[i];

      // First time we run, scene 0 is already fully populated on screen —
      // hold on it briefly, then advance. Every later run resets + animates.
      if (firstRun.current && i === 0) {
        firstRun.current = false;
        const dwell = 2800;
        schedule(() => runScene((i + 1) % SCENES.length), dwell);
        return;
      }

      // Reset for this scene.
      setSceneIdx(i);
      setModel(sc.modelStart);
      setShowQuestion(false);
      setStreamLen(0);

      // 1. Question fades in.
      schedule(() => setShowQuestion(true), 600);
      // 2. Mid-question, switch model.
      if (sc.modelSwitch) {
        schedule(() => setModel(sc.modelSwitch!), 1700);
      }
      // 3. Stream reply chunks (~700ms each).
      sc.reply.forEach((_, c) => {
        schedule(() => setStreamLen(c + 1), 2100 + c * 700);
      });
      // 4. Hold, then advance.
      const endAt = 2100 + sc.reply.length * 700 + 1800;
      schedule(() => runScene((i + 1) % SCENES.length), endAt);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        schedule(() => runScene(0), 300);
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      {/* Browser frame */}
      <div className="relative rounded-[20px] border hairline-strong bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 h-10 border-b hairline">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="ml-3 text-[11px] tracking-[0.18em] uppercase text-foreground/65 font-mono">
            askfinz extension · any tab
          </span>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65">
            <Sparkles className="h-3 w-3" />
            Side panel
          </span>
        </div>

        {/* Browser toolbar */}
        <div className="flex items-center gap-2 px-3 py-2 border-b hairline bg-foreground/[0.02]">
          <div className="flex items-center gap-0.5 text-foreground/65">
            <button className="p-1 rounded hover:bg-foreground/5" aria-hidden tabIndex={-1}>
              <ArrowLeft className="h-3.5 w-3.5" />
            </button>
            <button className="p-1 rounded hover:bg-foreground/5" aria-hidden tabIndex={-1}>
              <ArrowRight className="h-3.5 w-3.5" />
            </button>
            <button className="p-1 rounded hover:bg-foreground/5" aria-hidden tabIndex={-1}>
              <RotateCw className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="flex-1 flex items-center gap-2 rounded-full border hairline bg-background px-3 py-1 text-[11.5px] font-mono text-foreground/65 max-w-2xl">
            <span className="text-foreground/65">https://</span>
            <span key={scene.url} className="truncate animate-[blur-fade-in_0.4s_ease-out]">
              {scene.url}
            </span>
            <Star className="h-3 w-3 ml-auto text-foreground/35" />
          </div>
        </div>

        <div className="grid grid-cols-12 min-h-[460px] md:min-h-[520px]">
          {/* Page content — muted prose so the panel stands out */}
          <div key={scene.id} className="col-span-12 md:col-span-8 px-7 py-6 border-r hairline animate-[blur-fade-in_0.45s_ease-out]">
            <div className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.18em] uppercase text-foreground/35 font-mono">
              <scene.pageIcon className="h-3 w-3" />
              {scene.pageKicker}
            </div>
            <h3 className="mt-3 text-[19px] md:text-[22px] font-medium tracking-tight text-foreground/65 leading-snug max-w-md">
              {scene.pageTitle}
            </h3>

            {/* Stylised article body — muted bars and a chart placeholder */}
            <div className="mt-6 space-y-2.5">
              <div className="h-2 rounded bg-foreground/10 w-[94%]" />
              <div className="h-2 rounded bg-foreground/10 w-[88%]" />
              <div className="h-2 rounded bg-foreground/10 w-[92%]" />
              <div className="h-2 rounded bg-foreground/10 w-[71%]" />
              {scene.id === "sheet" ? (
                <div className="mt-5 rounded-md border hairline overflow-hidden">
                  <div className="grid grid-cols-[repeat(4,minmax(0,1fr))] text-[10px] font-mono text-foreground/65 bg-foreground/[0.04] border-b hairline">
                    {["Cohort", "Wk 1", "Wk 4", "Wk 8"].map((c) => (
                      <div key={c} className="px-2 py-1 border-r hairline last:border-r-0">{c}</div>
                    ))}
                  </div>
                  {[
                    ["Jan",   "100%", "52%", "31%"],
                    ["Feb",   "100%", "51%", "28%"],
                    ["Mar",   "100%", "38%", "—"],
                  ].map((row, i) => (
                    <div key={i} className="grid grid-cols-[repeat(4,minmax(0,1fr))] text-[10px] font-mono text-foreground/65 border-b hairline last:border-b-0">
                      {row.map((c, j) => (
                        <div key={j} className="px-2 py-1 border-r hairline last:border-r-0">{c}</div>
                      ))}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-5 h-28 rounded-md border hairline bg-foreground/[0.03] flex items-center justify-center text-[10px] font-mono text-foreground/35 tracking-[0.18em] uppercase">
                  fig. embedded chart
                </div>
              )}
              <div className="h-2 rounded bg-foreground/10 w-[80%]" />
              <div className="h-2 rounded bg-foreground/10 w-[86%]" />
              <div className="h-2 rounded bg-foreground/10 w-[74%]" />
              <div className="h-2 rounded bg-foreground/10 w-[91%]" />
            </div>
          </div>

          {/* askFinz side panel — the foreground */}
          <aside className="col-span-12 md:col-span-4 flex flex-col bg-background">
            {/* Panel chrome */}
            <div className="flex items-center gap-2 px-3 py-2 border-b hairline bg-foreground/[0.03]">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-foreground text-background text-[10px] font-mono font-semibold">
                aF
              </span>
              <span className="text-[11px] font-medium text-foreground/85">askFinz</span>
              <ModelPicker model={model} />
              <button className="ml-auto p-1 rounded hover:bg-foreground/5 text-foreground/65" aria-hidden tabIndex={-1}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Conversation */}
            <div className="flex-1 p-3 space-y-3 overflow-hidden">
              {showQuestion && (
                <div className="self-end ml-auto max-w-[90%] rounded-2xl rounded-br-md bg-foreground text-background px-3 py-2 text-[12px] leading-relaxed animate-[blur-fade-in_0.45s_ease-out]">
                  {scene.question}
                </div>
              )}
              {streamLen > 0 && (
                <div className="max-w-[95%] animate-[blur-fade-in_0.45s_ease-out]">
                  <div className="inline-flex items-center gap-1.5 mb-1 text-[9.5px] tracking-[0.14em] uppercase text-foreground/65">
                    <model.icon className="h-2.5 w-2.5" />
                    {model.label}
                  </div>
                  <div className="rounded-2xl rounded-tl-md border hairline bg-foreground/[0.02] px-3 py-2 text-[12px] leading-relaxed text-foreground/85">
                    {scene.reply.slice(0, streamLen).map((c, i) => {
                      if (c.kind === "cite") {
                        return (
                          <span
                            key={i}
                            className="inline-flex items-center rounded-md border hairline px-1.5 py-0.5 text-[9.5px] font-mono text-foreground/70 mx-0.5 align-baseline"
                          >
                            {c.text}
                          </span>
                        );
                      }
                      return <span key={i}>{c.text}</span>;
                    })}
                    {streamLen < totalChunks && (
                      <span className="inline-block h-3 w-1.5 bg-foreground/55 ml-0.5 align-middle animate-pulse" />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Composer */}
            <div className="border-t hairline px-3 py-2">
              <div className="flex items-end gap-1.5">
                <div className="flex-1 rounded-lg border hairline bg-foreground/[0.02] px-2.5 py-1.5 text-[11.5px] text-foreground/65 min-h-[32px]">
                  Ask about this page…
                </div>
                <button className="p-1.5 rounded-md text-foreground/65 hover:bg-foreground/5" aria-hidden tabIndex={-1}>
                  <Paperclip className="h-3.5 w-3.5" />
                </button>
                <button className="p-1.5 rounded-md text-foreground/65 hover:bg-foreground/5" aria-hidden tabIndex={-1}>
                  <AtSign className="h-3.5 w-3.5" />
                </button>
                <button className="p-1.5 rounded-md bg-foreground text-background" aria-hidden tabIndex={-1}>
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>

            {/* Caption */}
            <div className="px-3 py-2 border-t hairline flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] animate-pulse" />
              From any tab · no usage caps
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function ModelPicker({ model }: { model: ModelOpt }) {
  const Icon = model.icon;
  return (
    <button
      type="button"
      className="ml-1 inline-flex items-center gap-1 rounded-md border hairline bg-background px-2 py-1 text-[10.5px] text-foreground/75 hover:bg-foreground/5 transition"
    >
      <Icon className="h-3 w-3" />
      <span>{model.label}</span>
      <ChevronDown className="h-3 w-3 text-foreground/65" />
    </button>
  );
}
